import { type CustomFieldChangeset } from "@/shared/domain/customFields";
import { ISO_8601_DATETIME } from "@/types";

/**
 * When Roboto marks a Session complete on its own: once `inactivity_minutes`
 * have passed since a file was last added to it.
 *
 * Mirrors the Python `CompletionPolicy`.
 */
export interface CompletionPolicy {
  inactivity_minutes: number;
}

/** Longest inactivity a {@link CompletionPolicy} may wait for: one week. */
export const MAX_INACTIVITY_MINUTES = 7 * 24 * 60;

/**
 * `completed_by` of a Session Roboto marked complete because of its
 * completion policy.
 */
export const COMPLETED_BY_ROBOTO = "system@roboto.ai";

/**
 * Whether a Session is still receiving files. Mirrors the SDK `SessionStatus`.
 *
 * - `in_progress`: files may still be added; never announced ingested.
 * - `complete`: all files have been added; announced `session.ingested` once
 *   every ingestable file is ingested.
 */
export type SessionStatus = "in_progress" | "complete";

/**
 * Wire-format row for a Session — an operational time window of a Device
 * (drone flight, vehicle drive, robot run) that unifies the recordings
 * and auxiliary data produced during that window.
 *
 * Mirrors the Python `SessionRecord` at
 * `packages/roboto/src/roboto/experimental/sessions/record.py`.
 *
 * Bounds (`min_timestamp_ns`, `max_timestamp_ns`) are aggregated by the
 * service across the Session's file contributions and are `null` until
 * the Session has at least one contribution. They are `bigint` because
 * ns-epoch values exceed `Number.MAX_SAFE_INTEGER`; see
 * `shared/json/parseTimestampsAsBigInt.ts` for the precision-preserving
 * JSON reviver.
 */
export interface SessionRecord {
  /**
   * When the Session was last marked complete; `null` if it never was. A
   * Session put back in progress keeps it, so `status` says whether the
   * Session is complete now.
   */
  completed_at: ISO_8601_DATETIME | null;

  /**
   * User ID or service account that last marked the Session complete; `null`
   * if it never was.
   */
  completed_by: string | null;

  /**
   * When Roboto will mark the Session complete unless another file is added
   * first; `null` while it is complete, has no completion policy, or has no
   * files.
   */
  completes_at: ISO_8601_DATETIME | null;

  /**
   * When Roboto marks the Session complete on its own; `null` if only by
   * request.
   */
  completion_policy: CompletionPolicy | null;

  /** When the Session was created. */
  created: ISO_8601_DATETIME | null;

  /** User ID or service account that created the Session. */
  created_by: string;

  /**
   * Values for the Ready custom fields defined on Sessions in the owning org.
   * Every Ready field appears as a key; unset values surface as `null` rather
   * than being absent. Empty when the org has no Session custom fields.
   */
  custom_fields: Record<string, unknown>;

  /** Long-form, Markdown-rendered description; `null` when unset. */
  description: string | null;

  /** When the Session was last announced ingested; `null` until then. */
  ingested_at: ISO_8601_DATETIME | null;

  /** How many times the Session has been announced ingested. */
  ingestion_count: number;

  /** Aggregate upper bound (ns since epoch); `null` until composed. */
  max_timestamp_ns: bigint | null;

  /**
   * User-supplied key-value attributes. Not filterable or sortable from the
   * Sessions list; define a Session custom field for queryable structured
   * attributes.
   */
  metadata: Record<string, unknown>;

  /** Aggregate lower bound (ns since epoch); `null` until composed. */
  min_timestamp_ns: bigint | null;

  /** When the Session was last modified. */
  modified: ISO_8601_DATETIME | null;

  /** User ID or service account that last modified the Session. */
  modified_by: string;

  /** Human-readable label, capped at 120 characters by the server. */
  name: string | null;

  /** Organization that owns the Session. */
  org_id: string;

  /** Stable, unique identifier for the Session. */
  session_id: string;

  /**
   * Whether the Session is in progress or complete. Adding a file to a
   * complete Session puts it back in progress.
   */
  status: SessionStatus;

  /** Free-form labels; filterable in the Sessions list via `tags CONTAINS`. */
  tags: string[];
}

/**
 * Body for `POST /v1/sessions`. Mirrors the SDK `CreateSessionRequest`.
 *
 * Every field is optional; an empty body creates an unnamed session with no
 * device subjects. Files are attached separately via
 * `POST /v1/sessions/id/<id>/files`.
 */
export interface CreateSessionRequest {
  /**
   * When Roboto marks the session complete on its own. Omit it to give the
   * session the org's default completion policy; `null` marks it complete
   * only by request.
   */
  completion_policy?: CompletionPolicy | null;

  /**
   * Initial values for the Ready custom fields defined on Sessions in the
   * owning org. Unknown or non-Ready names, and values that don't match a
   * field's declared type, are rejected by the server.
   */
  custom_fields?: Record<string, unknown>;

  description?: string;

  /** Devices to attach as explicit subjects of the session. */
  device_ids?: string[];

  metadata?: Record<string, unknown>;

  /** Human-readable label; names over 120 characters are rejected. */
  name?: string;

  tags?: string[];
}

/**
 * One file in the body of `POST /v1/sessions/id/<id>/files`, distinct from this
 * domain's `SessionFile` read model. The SDK entry it mirrors also carries a
 * data range, a wall-clock anchor, and topic declarations; this client sends
 * whole files with an optional time window.
 *
 * Bounds are nanoseconds as the file's own data carries them, and `bigint`
 * because a file whose timestamps are Unix-epoch nanoseconds exceeds
 * `Number.MAX_SAFE_INTEGER`; `SessionService.addFilesToSession` serializes them
 * as raw JSON integers via `bigintReplacer`. Set both or omit both, since a
 * window carrying one bound is rejected; omitting both covers the file's entire
 * time window. The server reports the stored window back in wall clock, on the
 * `SessionFile` read model.
 */
export interface SessionFileDeclaration {
  file_id: string;
  min_file_timestamp_ns?: bigint | null;
  max_file_timestamp_ns?: bigint | null;
}

/**
 * Body for `PUT /v1/sessions/id/<id>`. Mirrors the SDK `SessionUpdate`.
 *
 * Field semantics (consistent across all clearable scalars):
 * - Field absent on the wire = "no change."
 * - Field set to `null` = clear the stored value.
 * - Field set to a value = replace the stored value.
 *
 * `metadata_changeset` is a partial diff (not a replacement): only the keys
 * named under `put_*` / `remove_*` are touched; everything else is left as-is.
 */
export interface UpdateSessionRequest {
  /** Set/clear Ready custom-field values; absent = no change. */
  custom_fields_changeset?: CustomFieldChangeset;
  description?: string | null;
  metadata_changeset?: {
    put_fields?: Record<string, unknown>;
    put_tags?: string[];
    remove_fields?: string[];
    remove_tags?: string[];
  };
  name?: string | null;
}

/**
 * Whether anyone opted the session into completion: it has a completion
 * policy, or it has been marked complete at least once. A session nobody
 * opted in, such as one created before sessions could be completed, shows
 * no status.
 */
export function tracksCompletion(
  session: Pick<SessionRecord, "completed_at" | "completion_policy">,
): boolean {
  return session.completed_at !== null || session.completion_policy !== null;
}
